import { distinctUntilChanged, map, shareReplay, type Observable } from 'rxjs'
import { state } from '@schmancy/state'
import {
	RADIUS_RUNGS,
	RADIUS_SHAPE_NAMES,
	RADIUS_SHAPES,
	isRadiusShape,
	rungRem,
	type RadiusShape,
	type RadiusToken,
} from './radius.ladder'

// The anchor: the signature nebula sweep, 40px. Every scalar rung is a ratio
// of this value (radius.ladder.ts) — setBase() rescales the whole ladder. The
// two shapes are geometry and carry no ratio, so setBase() cannot reach them:
// `rungRem` takes the base as an argument, `RADIUS_SHAPES` has nowhere to put
// one. A pill stays a pill at any base by construction, not by convention.
const BEZEL_BASE_PX = 40

type ShapeOverrides = Partial<Record<RadiusShape, string>>

interface RadiusSettings {
	base: number
	// `.local()` restores a persisted payload whole instead of merging it into
	// the initial value below, so a stored settings object carrying no `shapes`
	// key reads back with the field absent.
	shapes?: ShapeOverrides
}

const NO_SHAPE_OVERRIDES: ShapeOverrides = {}

// Device preference — survives clearStates() on an identity change (mirrors
// sound.service.ts/theme.service.ts: this is what a person set on THIS
// device, not something a different person signing in should reset).
const RadiusContext = state<RadiusSettings>('schmancy/radius').local(
	{ base: BEZEL_BASE_PX, shapes: NO_SHAPE_OVERRIDES },
	{ keepOnClear: true },
)

function propertyName(token: RadiusToken): string {
	return `--schmancy-radius-${token}`
}

// Runs on every base$ emission (wired in the constructor below) — the very
// first, synchronous one AND the later one the persisted read replaces it
// with — so a stored preference reaches `documentElement` on its own, with
// no separate boot call a consumer must remember (F7).
function reactToBase(basePx: number): void {
	const root = document.documentElement
	for (const rung of RADIUS_RUNGS) {
		if (basePx === BEZEL_BASE_PX) {
			// Back at the anchor: remove the override so the baked
			// `var(--schmancy-radius-x, <literal>)` fallback renders — never
			// write the same literal back explicitly.
			root.style.removeProperty(propertyName(rung))
		} else {
			root.style.setProperty(propertyName(rung), rungRem(rung, basePx))
		}
	}
}

// A shape reaches documentElement only where it diverges from the ladder's own
// value. Writing the documented value eagerly would put it in the root's inline
// style, which outranks every author stylesheet short of `!important` — someone
// who writes `:root { --schmancy-radius-full: 0.75rem }` would watch it do
// nothing and have no way to see why. Left unwritten, the baked
// `var(--schmancy-radius-full, calc(infinity * 1px))` fallback renders, and an
// override wins by existing at all — from a stylesheet, from a devtools edit,
// or from setShape() below. Same rule reactToBase() applies at the anchor.
function reactToShapes(overrides: ShapeOverrides): void {
	const root = document.documentElement
	for (const shape of RADIUS_SHAPE_NAMES) {
		const override = overrides[shape]
		if (override === undefined || override === RADIUS_SHAPES[shape]) {
			root.style.removeProperty(propertyName(shape))
		} else {
			root.style.setProperty(propertyName(shape), override)
		}
	}
}

class RadiusService {
	private static instance: RadiusService
	readonly defaultBase = BEZEL_BASE_PX

	public readonly base$ = RadiusContext.$.pipe(
		map(settings => settings.base),
		distinctUntilChanged(),
		shareReplay(1),
	)

	public readonly shapes$: Observable<ShapeOverrides> = RadiusContext.$.pipe(
		map(settings => settings.shapes ?? NO_SHAPE_OVERRIDES),
		distinctUntilChanged((a, b) => RADIUS_SHAPE_NAMES.every(shape => a[shape] === b[shape])),
		shareReplay(1),
	)

	get base(): number {
		return RadiusContext.value.base
	}

	private constructor() {
		this.base$.subscribe(reactToBase)
		this.shapes$.subscribe(reactToShapes)
	}

	static getInstance(): RadiusService {
		if (!RadiusService.instance) {
			RadiusService.instance = new RadiusService()
		}
		return RadiusService.instance
	}

	setBase(px: number): void {
		RadiusContext.set({ base: px })
	}

	setShape(shape: RadiusShape, value: string): void {
		RadiusContext.set({ shapes: { ...RadiusContext.value.shapes, [shape]: value } })
	}

	reset(): void {
		RadiusContext.set({ base: BEZEL_BASE_PX, shapes: NO_SHAPE_OVERRIDES })
	}

	// One call for both families. A consumer holding a token that selects a
	// corner — a component prop, a theme entry — asks the same question of
	// either, so the scalar/shape branch is answered here rather than at every
	// call site.
	rung(name: RadiusToken): string {
		if (isRadiusShape(name)) return RadiusContext.value.shapes?.[name] ?? RADIUS_SHAPES[name]
		return rungRem(name, this.base)
	}
}

export const radius = RadiusService.getInstance()
export const schmancyRadius = radius
